import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import CardGridSkeleton from '../shared/skeletons/CardGridSkeleton';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';
import formatters from '../shared/formatters';
import {PERIOD_ROLES} from '../shared/periods';

import ClientMonthlyClientsTable from './ClientMonthlyClientsTable';
import ClientMonthlySummaryCards from './ClientMonthlySummaryCards';
import PeriodTabs from './PeriodTabs';
import {summaryGridColumns} from './clientMonthlyShared';

// Orchestrates the per-client breakdown: a period segmented control on top, then the
// summary cards and client table for the selected month. All fetching, caching, and
// pagination live in the parent ClientMetrics scene; changing the period there swaps the
// cache key, which resets the table's sort/search via its remount key.
const ClientMonthlyBreakdownSection = (props) => {
  const {
    activePeriod,
    onPeriodChange,
    cacheKey = '',
    isLoading,
    isLoadingMore = false,
    errorMessage = '',
    month = '',
    metricDate = '',
    clients = [],
    totals = null,
    hasSnapshot = false,
    hasMore = false,
    onLoadMore = () => {},
    onRetry = () => {},
  } = props;

  const showActual = activePeriod !== 'next';
  const monthLabel = formatters.formatMonthLabel(month);
  const metricDateLabel = formatters.formatIsoDate(metricDate);

  const hasClients = clients.length > 0;
  const showInitialSkeleton = isLoading && !hasClients;
  const showFullError = !isLoading && !hasClients && Boolean(errorMessage);
  const showMissingSnapshot = !isLoading && !hasClients && !errorMessage && !hasSnapshot;
  const showEmpty = !isLoading && !hasClients && !errorMessage && hasSnapshot;

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.ClientMonthly.SectionTitle' />

      <Stack
        direction={{xs: 'column', sm: 'row'}}
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', sm: 'center'}}
        spacing={1.5}
        sx={{mb: 2}}
      >
        <Box sx={{minWidth: 0}}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '21px',
              color: '#1A1916',
              mb: 0.25,
            }}
          >
            <Translate expression='metrics.ClientMetrics.ClientMonthly.Title' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#A8A49E',
            }}
          >
            <Translate expression='metrics.ClientMetrics.ClientMonthly.Subtitle' substitute={{month: monthLabel}} />
          </Typography>
        </Box>
        <PeriodTabs value={activePeriod} onChange={onPeriodChange} />
      </Stack>

      {
        showInitialSkeleton &&
        <Stack spacing={2.5}>
          <CardGridSkeleton cardCount={showActual ? 7 : 5} gridTemplateColumns={summaryGridColumns} />
          <PanelSkeleton height={320} />
        </Stack>
      }

      {
        showFullError &&
        <Alert
          severity='warning'
          action={
            <Button color='inherit' size='small' onClick={onRetry}>
              <Translate expression='metrics.ClientMetrics.ClientMonthly.Retry' />
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      }

      {
        showMissingSnapshot &&
        <Alert severity='info'>
          <Translate
            expression='metrics.ClientMetrics.ClientMonthly.EmptyStates.MissingSnapshot'
            substitute={{date: metricDateLabel}}
          />
        </Alert>
      }

      {
        showEmpty &&
        <Alert severity='info'>
          <Translate
            expression='metrics.ClientMetrics.ClientMonthly.EmptyStates.NoClients'
            substitute={{month: monthLabel}}
          />
        </Alert>
      }

      {
        hasClients &&
        <Stack spacing={2.5}>
          <ClientMonthlySummaryCards totals={totals} showActual={showActual} />
          <ClientMonthlyClientsTable
            key={cacheKey}
            clients={clients}
            totals={totals}
            showActual={showActual}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            errorMessage={errorMessage}
            onLoadMore={onLoadMore}
          />
        </Stack>
      }
    </Box>
  );
};

ClientMonthlyBreakdownSection.propTypes = {
  activePeriod: PropTypes.oneOf(PERIOD_ROLES).isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  cacheKey: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool,
  errorMessage: PropTypes.string,
  month: PropTypes.string,
  metricDate: PropTypes.string,
  clients: PropTypes.array,
  totals: PropTypes.object,
  hasSnapshot: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onRetry: PropTypes.func,
};

export default React.memo(ClientMonthlyBreakdownSection);
